/**
 * FIX-061 t517u (1): a frame-select request that is still in flight must survive a re-render / remount of the Gate 1 card (a poll re-keying the list, a
 * tab switch, a page reload). Until the server has recorded the selection its durable status still reads "awaiting your frame selection", so a fresh
 * card instance would offer the picker again (the S4 re-pick of 2026-10-21). The pending marker lives in `sessionStorage` (this tab only, never sent
 * anywhere) and expires by itself, so a lost response can never block the card: after `PENDING_FRAME_SELECTION_MAX_AGE_MS` the durable state decides again.
 * Rollback: set `PERSIST_PENDING_FRAME_SELECTION` to false (the card's previous, component-local behaviour).
 */
export const PERSIST_PENDING_FRAME_SELECTION = true;
/** Longer than the server's in-request work (one 12 s person check + download + guarded write) with margin; short enough that a lost response self-heals. */
export const PENDING_FRAME_SELECTION_MAX_AGE_MS = 90_000;

const KEY_PREFIX = "emma.gate1.frameSelectPending.";

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" && window.sessionStorage !== undefined ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** Records that a frame-select request for this artifact was just sent. Never throws. */
export function markFrameSelectionPending(artifactId: string, nowMs: number): void {
  if (!PERSIST_PENDING_FRAME_SELECTION) return;
  try {
    storage()?.setItem(KEY_PREFIX + artifactId, String(nowMs));
  } catch {
    // storage full / blocked: the component-local state still covers the common case
  }
}

/** Clears the marker (the request settled, successfully or not). Never throws. */
export function clearFrameSelectionPending(artifactId: string): void {
  try {
    storage()?.removeItem(KEY_PREFIX + artifactId);
  } catch {
    // ignore
  }
}

/** The start time of a still-fresh pending request for this artifact, or null (none, expired, unreadable, or the feature is off). */
export function pendingFrameSelectionStartedAtMs(artifactId: string, nowMs: number): number | null {
  if (!PERSIST_PENDING_FRAME_SELECTION) return null;
  try {
    const raw = storage()?.getItem(KEY_PREFIX + artifactId) ?? null;
    if (raw === null) return null;
    const startedAtMs = Number(raw);
    if (!Number.isFinite(startedAtMs) || nowMs - startedAtMs > PENDING_FRAME_SELECTION_MAX_AGE_MS || startedAtMs - nowMs > 5_000) {
      storage()?.removeItem(KEY_PREFIX + artifactId);
      return null;
    }
    return startedAtMs;
  } catch {
    return null;
  }
}
